/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Col,
  Input,
  Layout,
  message,
  Modal,
  Row,
  Select,
  Typography,
} from 'antd';
import queryString from 'query-string';

import PdButton from 'components/pd-button';
import { InspectionCreateEdit } from './create-edit';
import { TableList } from 'components/table-list';
import { UpdateInspectionSerialNumberForm } from './updateInspectionSerialNumber';

import { ProfileContext } from 'contexts/profile';

import { errHandling } from 'utils';
import InspectionService from 'services/inspection';

import './style.scss';

const { Content } = Layout;
const { Option } = Select;
const { Search } = Input;
const { Text, Title } = Typography;
const { confirm } = Modal;

const STATUS = {
  AWAITING: 'awaiting_inspection',
  DONE: 'done_inspection',
  ONGOING: 'ongoing_inspection',
};

export function InspectionDetail() {
  const location = useLocation();
  const { fetchProfile } = useContext(ProfileContext);

  const [inspectionDetail, setInspectionDetail] = useState([]);
  const [loadStatus, setLoadStatus] = useState(true);
  const [loadingButton, setLoadingButton] = useState(false);
  const [modalContent, setModalContent] = useState({});
  const [productListOnAwaitingStatus, setProductListOnAwaitingStatus] =
    useState([]);
  const [rentDuration, setRentDuration] = useState({});
  const [serialNumberModalContent, setSerialNumberModalContent] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [showSerialNumberModal, setShowSerialNumberModal] = useState(false);

  const navigate = useNavigate();
  const parsed = queryString.parse(location.search);

  const { id: orderId, status: pageStatus } = parsed;

  const buttonAction = (row, type, label) => (
    <Row className="button-table">
      <Col lg={24}>
        <PdButton
          className="height-32"
          full
          onClick={
            type === 'danger'
              ? handleDeletePenalty.bind(this, row)
              : handleOpenModal.bind(this, row)
          }
          type={type}>
          {label}
        </PdButton>
      </Col>
    </Row>
  );

  const columns = [
    {
      name: 'Inspection List',
      selector: row => row.damage_penalty.title,
    },
    {
      name: 'Description',
      selector: row => row.damage_penalty.description,
    },
    {
      name: '',
      cell: (row) => buttonAction(row, 'primary', 'See Detail'),
      width: '15%',
    },
    {
      name: '',
      cell: (row) =>
        pageStatus !== STATUS.ONGOING
          ? null
          : buttonAction(row, 'danger', 'Delete'),
      width: '15%',
    },
  ];

  function handleOpenModal(inspection) {
    setModalContent({ inspection, orderId });
    setShowModal(true);
  }

  function handleCloseModal() {
    setShowModal(false);
    setModalContent({});
  }

  function handleCloseCreateEdit() {
    fetchInspectionList();
    handleCloseModal();
  }

  async function fetchInspectionList() {
    setLoadStatus(true);
    try {
      const {
        data: { data },
      } = await InspectionService.getRentOrderDetails(orderId);
      setInspectionDetail(data);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  async function handleSubmit() {
    setLoadingButton(true);
    try {
      await InspectionService.finishInspection(orderId);
      navigate({
        pathname: '/inspections',
      });
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadingButton(false);
    }
  }

  async function handleStartInspection() {
    try {
      await InspectionService.startInspection(orderId);
      parsed.status = STATUS.ONGOING;
      const stringified = queryString.stringify(parsed);
      navigate({
        pathname: '/inspections/detail',
        search: stringified,
      });
    } catch (error) {
      errHandling(error);
    }
  }

  async function handleDeletePenalty({ id } = {}) {
    try {
      await InspectionService.deletePenalty(id);
      fetchInspectionList();
    } catch (error) {
      errHandling(error);
    }
  }

  async function fetchRentDurationList() {
    try {
      const { data: { data: { rent_duration, order_products } = {} } = {} } =
        await InspectionService.getRentOrderInspectionDetails(orderId);
      setRentDuration(rent_duration);
      setProductListOnAwaitingStatus(order_products);
    } catch (error) {
      errHandling(error);
    }
  }

  function handleOnClickSetSerialNumber({ id, serial_number }) {
    setSerialNumberModalContent({ id, serial_number });
    setShowSerialNumberModal(true);
  }

  function onSubmitSuccessSerialNumber() {
    setShowSerialNumberModal(false);
    fetchInspectionList();
    fetchRentDurationList();
  }

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchInspectionList();
    fetchRentDurationList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageStatus, orderId]);

  return (
    <Layout className="main-container pd-cms-penalty-detail">
      <div className="content">
        <div className="pageTitle">{`Inspection for: ${orderId}`}</div>
        <Content className="pageContent section-container">
          {pageStatus === STATUS.AWAITING && (
            <Row className="start-inspection-button">
              <PdButton
                onClick={handleStartInspection}
                className="height-32"
                type="primary pd-padding-right-md pd-padding-left-md">
                Start Inspection
              </PdButton>
            </Row>
          )}

          {pageStatus === STATUS.AWAITING &&
            productListOnAwaitingStatus.map(
              ({ product: { title } = {}, quantity } = {}, idx) => (
                <div key={idx} className="pd-margin-bottom-md inner-section">
                  <Row gutter={[18, 36]} type="flex" align="middle">
                    <Col lg={19}>
                      <Title level={3}>{title}</Title>
                      <Text type="secondary">Quantity: {quantity}</Text>
                    </Col>
                  </Row>
                  <Row className="pd-margin-bottom-lg">
                    <Text type="secondary">
                      Rent: {rentDuration.duration} days
                    </Text>
                  </Row>
                </div>
              )
            )}
          {inspectionDetail.map(
            ({
              id,
              penalties = [],
              product: { title, id: product_id } = {},
              serial_number,
            } = {}) => (
              <div key={id} className="pd-margin-bottom-md inner-section">
                <Row gutter={[18, 36]} type="flex" align="middle">
                  <Col lg={14}>
                    <Title level={3}>{title}</Title>
                  </Col>
                  <Col lg={5}>
                    <PdButton
                      className="height-32"
                      disabled={pageStatus !== STATUS.ONGOING}
                      full
                      onClick={handleOnClickSetSerialNumber.bind(this, {
                        id,
                        serial_number,
                      })}
                      type="primary">
                      Set Serial Number
                    </PdButton>
                  </Col>
                  <Col lg={5}>
                    <PdButton
                      className="height-32"
                      type="danger"
                      full
                      disabled={pageStatus !== STATUS.ONGOING}
                      onClick={handleOpenModal.bind(this, {
                        order_product_inspection_id: id,
                        product_id,
                      })}>
                      Add Inspection Result
                    </PdButton>
                  </Col>
                </Row>
                <Row className="pd-margin-bottom-xs">
                  <Text type="secondary">Serial Number: {serial_number}</Text>
                </Row>
                <Row className="pd-margin-bottom-lg">
                  <Text type="secondary">
                    Rent: {rentDuration.duration} days
                  </Text>
                </Row>

                <Row>
                  {!loadStatus && (
                    <TableList
                      columns={columns}
                      loadingOrder={loadStatus}
                      orderList={penalties.map((pen) => ({
                        ...pen,
                        product_id,
                      }))}
                      responsive={false}
                    />
                  )}
                </Row>
              </div>
            )
          )}
          <Row className="pd-margin-top-xl" type="flex" gutter={8}>
            <Col lg={8}>
              <PdButton
                className="height-32"
                full
                type="danger"
                onClick={() => navigate(-1)}>
                Back
              </PdButton>
            </Col>
            <Col lg={8}>
              <PdButton
                className="height-32"
                disabled={pageStatus !== STATUS.ONGOING}
                type="primary"
                full
                onClick={handleSubmit}>
                Send to Admin
              </PdButton>
            </Col>
          </Row>
        </Content>
      </div>
      {/* Modal Section */}
      {showModal && (
        <Modal
          open={showModal}
          wrapClassName="hidden-wrap-modal"
          centered
          onCancel={handleCloseModal}
          footer={false}>
          <InspectionCreateEdit
            closeModal={handleCloseCreateEdit}
            data={modalContent}
            warehouseList={[]}
            pageStatus={pageStatus}
          />
        </Modal>
      )}
      {showSerialNumberModal && (
        <Modal
          centered
          footer={false}
          onCancel={setShowSerialNumberModal.bind(this, false)}
          title="Set Serial Number"
          open={showSerialNumberModal}
          wrapClassName="hidden-wrap-modal">
          <UpdateInspectionSerialNumberForm
            inspection={serialNumberModalContent}
            onSubmitSuccess={onSubmitSuccessSerialNumber}
          />
        </Modal>
      )}
    </Layout>
  );
}

export default { InspectionDetail };
